import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime, fmtMoney } from "@/lib/format";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending_activation: "bg-amber-100 text-amber-800",
  active: "bg-green-100 text-green-800",
  replaced: "bg-slate-100 text-slate-600",
  expired: "bg-red-100 text-red-700",
};

export default function AdminSubscriptions() {
  const [requests, setRequests] = useState(null);
  const [activating, setActivating] = useState("");

  const load = () => api.get("/admin/subscription-requests").then((r) => setRequests(r.data.requests)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const activate = async (id) => {
    setActivating(id);
    try {
      await api.post(`/admin/subscription-requests/${id}/activate`);
      toast.success("Subscription activated — plan limits unlocked");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActivating("");
    }
  };

  if (!requests) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Subscription Requests</h1>
        <p className="mt-1 text-sm text-muted-foreground">Activate enterprise subscriptions once payment is confirmed.</p>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white" data-testid="subscription-requests-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Enterprise</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Price</th>
              <th className="px-4 py-3 font-medium">Requested By</th>
              <th className="px-4 py-3 font-medium">Requested At</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No subscription requests yet.</td></tr>
            )}
            {requests.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`subscription-request-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.enterprise_name}</td>
                <td className="px-4 py-3">{r.plan_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.plan_name === "Custom" ? "Custom" : "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.requested_by}</td>
                <td className="px-4 py-3 text-muted-foreground">{fmtDateTime(r.requested_at)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status] || "bg-slate-100 text-slate-600"}`}>
                    {r.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {r.status === "pending_activation" && (
                    <Button size="sm" data-testid={`activate-subscription-${r.id}`} disabled={activating === r.id}
                      className="bg-slate-900 hover:bg-slate-800" onClick={() => activate(r.id)}>
                      {activating === r.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
                      Activate
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
